import { useState, useEffect, useRef, useCallback } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import { Card } from './ui/card';
import { liveChatAPI } from '../api';
import { useAuth } from '../context/AuthContext';
import { playNotificationSound } from '../utils/notificationSound';
import { toast } from 'sonner';
import { 
  MessageCircle, X, Send, Minimize2, Maximize2, 
  User, Loader2, CheckCircle, Clock, Volume2, VolumeX, Bell, Users
} from 'lucide-react';

/**
 * AgentChatWidget - Floating live chat widget for CRM agents
 * Shows incoming chat requests and allows agents to respond
 */
export const AgentChatWidget = () => {
  const { user } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [waitingSessions, setWaitingSessions] = useState([]);
  const [activeSessions, setActiveSessions] = useState([]);
  const [currentSession, setCurrentSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [unreadCount, setUnreadCount] = useState(0);
  const messagesEndRef = useRef(null);
  const pollInterval = useRef(null);
  const previousWaitingCount = useRef(0);

  // Check for waiting/active chat sessions
  const fetchSessions = useCallback(async () => {
    try {
      const [waitingRes, activeRes] = await Promise.all([
        liveChatAPI.getSessions('waiting'),
        liveChatAPI.getSessions('active')
      ]);
      
      const waiting = waitingRes.data || [];
      const active = activeRes.data?.filter(s => s.assigned_to === user?.id) || [];
      
      // Play sound for new waiting chats
      if (waiting.length > previousWaitingCount.current && previousWaitingCount.current >= 0) {
        if (soundEnabled) {
          playNotificationSound('chat', 0.3);
        }
        if (!isOpen) {
          toast.info('New chat request!', {
            description: 'A customer is waiting for support',
            action: {
              label: 'View',
              onClick: () => setIsOpen(true)
            }
          });
        }
      }
      
      previousWaitingCount.current = waiting.length;
      setWaitingSessions(waiting);
      setActiveSessions(active);
      setUnreadCount(waiting.length);
    } catch (err) {
      console.debug('Could not fetch sessions:', err);
    }
  }, [user, soundEnabled, isOpen]);

  // Fetch messages for current session
  const fetchMessages = useCallback(async () => {
    if (!currentSession?.id) return;
    
    try {
      const res = await liveChatAPI.getMessages(currentSession.id);
      setMessages(res.data.messages || res.data || []);
    } catch (err) {
      console.error('Failed to fetch messages:', err);
    }
  }, [currentSession]);

  // Poll for sessions and messages
  useEffect(() => {
    fetchSessions();
    pollInterval.current = setInterval(fetchSessions, 5000);
    
    return () => {
      if (pollInterval.current) clearInterval(pollInterval.current);
    };
  }, [fetchSessions]);

  useEffect(() => {
    if (currentSession?.id) {
      fetchMessages();
      const msgInterval = setInterval(fetchMessages, 3000);
      return () => clearInterval(msgInterval);
    }
  }, [currentSession, fetchMessages]);

  // Scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Accept a waiting chat
  const acceptChat = async (session) => {
    try {
      await liveChatAPI.assignChat(session.id);
      setCurrentSession(session);
      fetchSessions();
      fetchMessages();
      toast.success('Chat assigned to you');
    } catch (err) {
      toast.error('Failed to accept chat');
    }
  };

  // Send a message
  const sendMessage = async (e) => {
    e?.preventDefault();
    if (!newMessage.trim() || !currentSession?.id) return;
    
    setSending(true);
    try {
      await liveChatAPI.sendMessage(currentSession.id, {
        content: newMessage,
        sender_type: 'agent',
        sender_id: user?.id,
        sender_name: `${user?.first_name || ''} ${user?.last_name || ''}`.trim() || user?.email
      });
      setNewMessage('');
      fetchMessages();
    } catch (err) {
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  // Close current chat
  const closeChat = async () => {
    if (currentSession?.id) {
      try {
        await liveChatAPI.closeChat(currentSession.id);
        toast.success('Chat closed');
      } catch (err) {
        console.error('Failed to close chat:', err);
      }
    }
    setCurrentSession(null);
    setMessages([]);
    fetchSessions();
  };

  // Don't render for non-support roles
  if (!user || !['admin', 'support_agent', 'technical_support', 'sales_manager', 'manager'].includes(user.role)) {
    return null;
  }

  return (
    <>
      {/* Floating Chat Button - positioned to not overlap with customer chat */}
      <div className="fixed bottom-6 right-24 z-50">
        {/* Notification Badge */}
        {unreadCount > 0 && !isOpen && (
          <div className="absolute -top-2 -right-2 animate-bounce">
            <Badge className="bg-red-500 text-white px-2 py-1">
              {unreadCount}
            </Badge>
          </div>
        )}
        
        {!isOpen ? (
          <Button
            onClick={() => setIsOpen(true)}
            className="h-14 w-14 rounded-full shadow-lg bg-primary hover:bg-primary/90"
            data-testid="agent-chat-toggle"
          >
            <MessageCircle className="h-6 w-6" />
          </Button>
        ) : (
          <Card className={`w-96 shadow-2xl transition-all duration-300 ${isMinimized ? 'h-14' : 'h-[500px]'}`}>
            {/* Header */}
            <div className="flex items-center justify-between p-3 border-b bg-primary text-white rounded-t-lg">
              <div className="flex items-center gap-2">
                <Users className="h-5 w-5" />
                <span className="font-medium">Live Chat Support</span>
                {unreadCount > 0 && (
                  <Badge className="bg-red-500">{unreadCount}</Badge>
                )}
              </div>
              <div className="flex items-center gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setSoundEnabled(!soundEnabled)}
                  className="text-white hover:bg-white/20 h-8 w-8 p-0"
                >
                  {soundEnabled ? <Volume2 className="h-4 w-4" /> : <VolumeX className="h-4 w-4" />}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setIsMinimized(!isMinimized)}
                  className="text-white hover:bg-white/20 h-8 w-8 p-0"
                >
                  {isMinimized ? <Maximize2 className="h-4 w-4" /> : <Minimize2 className="h-4 w-4" />}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => { setIsOpen(false); setCurrentSession(null); }}
                  className="text-white hover:bg-white/20 h-8 w-8 p-0"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </div>

            {!isMinimized && (
              <div className="flex flex-col h-[calc(100%-56px)]">
                {!currentSession ? (
                  /* Session List View */
                  <ScrollArea className="flex-1 p-3">
                    {/* Waiting Sessions */}
                    {waitingSessions.length > 0 && (
                      <div className="mb-4">
                        <h4 className="text-sm font-semibold text-amber-600 mb-2 flex items-center gap-1">
                          <Bell className="h-4 w-4" /> Waiting ({waitingSessions.length})
                        </h4>
                        {waitingSessions.map(session => (
                          <div
                            key={session.id}
                            className="p-3 mb-2 bg-amber-50 border border-amber-200 rounded-lg"
                          >
                            <div className="flex justify-between items-start">
                              <div>
                                <p className="font-medium">{session.visitor_name || 'Visitor'}</p>
                                <p className="text-xs text-muted-foreground">{session.visitor_email}</p>
                              </div>
                              <Button
                                size="sm"
                                onClick={() => acceptChat(session)}
                                data-testid={`accept-chat-${session.id}`}
                              >
                                Accept
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Active Sessions */}
                    {activeSessions.length > 0 && (
                      <div>
                        <h4 className="text-sm font-semibold text-emerald-600 mb-2 flex items-center gap-1">
                          <CheckCircle className="h-4 w-4" /> Active ({activeSessions.length})
                        </h4>
                        {activeSessions.map(session => (
                          <div
                            key={session.id}
                            className="p-3 mb-2 bg-emerald-50 border border-emerald-200 rounded-lg cursor-pointer hover:bg-emerald-100"
                            onClick={() => { setCurrentSession(session); fetchMessages(); }}
                          >
                            <p className="font-medium">{session.visitor_name || 'Visitor'}</p>
                            <p className="text-xs text-muted-foreground">
                              {session.message_count || 0} messages
                            </p>
                          </div>
                        ))}
                      </div>
                    )}

                    {waitingSessions.length === 0 && activeSessions.length === 0 && (
                      <div className="text-center text-muted-foreground py-8">
                        <MessageCircle className="h-12 w-12 mx-auto mb-2 opacity-50" />
                        <p>No active chats</p>
                        <p className="text-xs">Waiting for customers...</p>
                      </div>
                    )}
                  </ScrollArea>
                ) : (
                  /* Chat View */
                  <>
                    {/* Chat Header */}
                    <div className="p-2 border-b bg-muted/50 flex justify-between items-center">
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setCurrentSession(null)}
                        >
                          ← Back
                        </Button>
                        <span className="font-medium">{currentSession.visitor_name || 'Visitor'}</span>
                      </div>
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={closeChat}
                      >
                        End Chat
                      </Button>
                    </div>

                    {/* Messages */}
                    <ScrollArea className="flex-1 p-3">
                      {messages.map((msg, idx) => (
                        <div
                          key={msg.id || idx}
                          className={`mb-3 ${
                            msg.sender_type === 'agent'
                              ? 'ml-8'
                              : msg.sender_type === 'system'
                              ? 'mx-auto max-w-[80%]'
                              : 'mr-8'
                          }`}
                        >
                          <div
                            className={`p-3 rounded-lg ${
                              msg.sender_type === 'agent'
                                ? 'bg-primary text-white'
                                : msg.sender_type === 'system'
                                ? 'bg-muted text-muted-foreground text-center text-sm'
                                : 'bg-muted'
                            }`}
                          >
                            <p className="text-sm">{msg.content}</p>
                          </div>
                          {msg.sender_type !== 'system' && (
                            <p className="text-xs text-muted-foreground mt-1">
                              {msg.sender_type === 'agent' ? 'You' : msg.sender_name || 'Visitor'}
                            </p>
                          )}
                        </div>
                      ))}
                      <div ref={messagesEndRef} />
                    </ScrollArea>

                    {/* Input */}
                    <form onSubmit={sendMessage} className="p-3 border-t">
                      <div className="flex gap-2">
                        <Input
                          value={newMessage}
                          onChange={(e) => setNewMessage(e.target.value)}
                          placeholder="Type a message..."
                          disabled={sending}
                          data-testid="agent-chat-input"
                        />
                        <Button type="submit" disabled={sending || !newMessage.trim()}>
                          {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                        </Button>
                      </div>
                    </form>
                  </>
                )}
              </div>
            )}
          </Card>
        )}
      </div>
    </>
  );
};

export default AgentChatWidget;
